import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, TextField } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const validationSchema = Yup.object({
  // Example: add validation for fields you want to edit
  status: Yup.string().required('Required'),
  verified: Yup.boolean(),
});

export default function RecordDetailPage() {
  const { id, client } = useParams();
  const [record, setRecord] = useState(null);
  const [fieldsSkipped, setFieldsSkipped] = useState([]);
  const [fieldsInput, setFieldsInput] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000';
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/record/${id}?client=${client}`)
      .then(res => res.json())
      .then(data => {
        setRecord(data);
        // Find last verification_history and its fields_skipped_empty (top-level)
        const history = data?.verification_history;
        if (Array.isArray(history) && history.length > 0) {
          const last = history[history.length - 1];
          if (Array.isArray(last.fields_skipped_empty)) {
            setFieldsSkipped(last.fields_skipped_empty);
            // Initialize input state for each key
            const initialInputs = {};
            last.fields_skipped_empty.forEach(k => { initialInputs[k] = ''; });
            setFieldsInput(initialInputs);
          }
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [id, client]);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!record) return <Typography>No record found.</Typography>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>Record Detail</Typography>
      {/* Debug: Show last verification_history record */}
      {record?.structure?.verification_history && record.structure.verification_history.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Debug: Last verification_history</Typography>
          <pre>{JSON.stringify(record.structure.verification_history[record.structure.verification_history.length - 1], null, 2)}</pre>
        </Box>
      )}
      {/* Display fields_skipped_empty as editable inputs */}
      {fieldsSkipped.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>Fields Skipped (Please fill):</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {fieldsSkipped.map(key => (
              <TextField
                key={key}
                label={key}
                value={fieldsInput[key] || ''}
                onChange={e => setFieldsInput(inputs => ({ ...inputs, [key]: e.target.value }))}
                sx={{ mb: 2 }}
              />
            ))}
          </Box>
          <Button
            variant="contained"
            sx={{ mt: 1 }}
            onClick={async () => {
              // Insert filled keys into record.structure
              const updatedStructure = { ...record.structure };
              for (const key of fieldsSkipped) {
                if (fieldsInput[key]) {
                  // PATCH each field individually
                  await fetch(`${API_BASE_URL}/api/record/${id}?client=${client}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ field: key, value: fieldsInput[key] }),
                  });
                  updatedStructure[key] = fieldsInput[key];
                }
              }
              // Optionally refresh record
              setRecord(r => ({ ...r, structure: updatedStructure }));
            }}
          >Update Skipped Fields</Button>
        </Box>
      )}
      <Formik
        initialValues={{
          status: record.status || '',
          verified: !!record.verified,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          // PATCH status and verified fields
          if ('status' in values) {
            await fetch(`${API_BASE_URL}/api/record/${id}?client=${client}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ field: 'status', value: values.status }),
            });
          }
          if ('verified' in values) {
            await fetch(`${API_BASE_URL}/api/record/${id}?client=${client}`, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ field: 'verified', value: values.verified }),
            });
          }
          setSubmitting(false);
        }}
      >
        {({ isSubmitting, errors, touched }) => (
          <Form>
            <Box sx={{ mb: 2 }}>
              <Field name="status">
                {({ field }) => (
                  <TextField
                    {...field}
                    label="Status"
                    error={touched.status && !!errors.status}
                    helperText={touched.status && errors.status}
                    fullWidth
                  />
                )}
              </Field>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Field name="verified" type="checkbox">
                {({ field }) => (
                  <label>
                    <input type="checkbox" {...field} /> Verified
                  </label>
                )}
              </Field>
            </Box>
            <Button type="submit" variant="contained" disabled={isSubmitting}>Update</Button>
          </Form>
        )}
      </Formik>
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1">Raw JSON:</Typography>
        <pre style={{ background: '#f5f5f5', padding: 16 }}>{JSON.stringify(record, null, 2)}</pre>
      </Box>
    </Box>
  );
}
